import type {
  IsolationController,
  IsolationNodeSummary,
} from 'features/knowledgeDocumentViewer/graph/IsolationControllerContext'
import isEqual from 'lodash/isEqual'
import { useCallback, useMemo, useRef, useState } from 'react'

import {
  isolationSelection,
  type LotoPointChanges,
  type LotoPointKey,
  moveIsolationPoint,
  removeIsolationPoint,
  updateIsolationPoint,
} from './lotoIsolationPoints'
import { addAttachments } from './lotoReportDraft'
import type {
  LotoAttachment,
  LotoReport,
  LotoReportEntry,
  LotoReportScope,
} from './lotoReportSchemas'
import {
  type LotoPickMode,
  pickModeToColor,
  removeTargetEquipment,
  toggleLotoNode,
} from './lotoTargetEquipment'
import { useLotoReports } from './useLotoReports'

export type LotoSession = {
  reportId: string
  report: LotoReportEntry | undefined
  hasChanges: boolean
  saving: boolean
  saveError: Error | undefined
  save: () => Promise<boolean>
  discard: () => void
  addPage: (attachment: LotoAttachment) => void
  updatePoint: (key: LotoPointKey, changes: LotoPointChanges) => void
  removePoint: (key: LotoPointKey) => void
  movePoint: (from: number, to: number) => void
  toggleNode: (node: IsolationNodeSummary, systemDocumentId: string) => void
  pickMode: LotoPickMode
  setPickMode: (mode: LotoPickMode) => void
  removeTarget: (key: LotoPointKey) => void
}

type ReportDraft = {
  working?: LotoReportEntry
  saveError?: Error
}

type DraftUpdate = (draft: ReportDraft) => ReportDraft

type UpdateDraft = (reportId: string, update: DraftUpdate) => void

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function useReportDraft(
  reportId: string | undefined
): [ReportDraft, UpdateDraft] {
  const [reportIdToDraft, setReportIdToDraft] = useState<
    Partial<Record<string, ReportDraft>>
  >({})
  const updateDraft = useCallback<UpdateDraft>(
    (id, update) =>
      setReportIdToDraft(({ [id]: draft = {}, ...others }) => {
        const next = update(draft)
        return next.working ? { ...others, [id]: next } : others
      }),
    []
  )
  const draft = reportId === undefined ? undefined : reportIdToDraft[reportId]
  return [draft ?? {}, updateDraft]
}

function useSavingReportIds(): {
  savingIds: ReadonlySet<string>
  startSaving: (reportId: string) => boolean
  finishSaving: (reportId: string) => void
} {
  const savingRef = useRef(new Set<string>())
  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(new Set())
  const startSaving = useCallback((reportId: string): boolean => {
    if (savingRef.current.has(reportId)) return false
    savingRef.current.add(reportId)
    setSavingIds(new Set(savingRef.current))
    return true
  }, [])
  const finishSaving = useCallback((reportId: string): void => {
    savingRef.current.delete(reportId)
    setSavingIds(new Set(savingRef.current))
  }, [])
  return { savingIds, startSaving, finishSaving }
}

function useSaveWorking(
  working: LotoReportEntry | undefined,
  updateDraft: UpdateDraft,
  save: (entry: LotoReportEntry) => Promise<void>
): { savingIds: ReadonlySet<string>; saveWorking: () => Promise<boolean> } {
  const { savingIds, startSaving, finishSaving } = useSavingReportIds()
  const saveWorking = useCallback(async (): Promise<boolean> => {
    if (!working || !startSaving(working.id)) return false
    const forThisReport = (update: DraftUpdate): void =>
      updateDraft(working.id, update)
    forThisReport((draft) => ({ ...draft, saveError: undefined }))
    try {
      await save(working)
      forThisReport((draft) => (draft.working === working ? {} : draft))
      return true
    } catch (error) {
      forThisReport((draft) =>
        draft.working ? { ...draft, saveError: toError(error) } : draft
      )
      return false
    } finally {
      finishSaving(working.id)
    }
  }, [finishSaving, save, startSaving, updateDraft, working])
  return { savingIds, saveWorking }
}

function usePickMode(
  reportId: string | undefined
): [LotoPickMode, (mode: LotoPickMode) => void] {
  const [equipmentReportId, setEquipmentReportId] = useState<string>()
  if (equipmentReportId !== undefined && equipmentReportId !== reportId)
    setEquipmentReportId(undefined)
  const setPickMode = useCallback(
    (mode: LotoPickMode) =>
      setEquipmentReportId(mode === 'equipment' ? reportId : undefined),
    [reportId]
  )
  const picking = reportId !== undefined && equipmentReportId === reportId
  return [picking ? 'equipment' : 'points', setPickMode]
}

export function useLotoSession(
  scope: LotoReportScope | undefined,
  reportId: string | undefined
): LotoSession | undefined {
  const { reports, save } = useLotoReports(scope, reportId !== undefined)
  const stored = reports.find(({ id }) => id === reportId)
  const [{ working, saveError }, updateDraft] = useReportDraft(reportId)
  const { savingIds, saveWorking } = useSaveWorking(working, updateDraft, save)
  const report = working ?? stored
  const hasChanges =
    working !== undefined && !isEqual(working.data, stored?.data)
  const saving = reportId !== undefined && savingIds.has(reportId)
  const [pickMode, setPickMode] = usePickMode(reportId)

  const edit = useCallback(
    (change: (data: LotoReport) => LotoReport): void => {
      if (reportId === undefined) return
      updateDraft(reportId, (draft) => {
        const latest = draft.working ?? stored
        if (!latest) return draft
        return { working: { ...latest, data: change(latest.data) } }
      })
    },
    [reportId, stored, updateDraft]
  )
  const updatePoint = useCallback(
    (key: LotoPointKey, changes: LotoPointChanges) =>
      edit((data) => updateIsolationPoint(data, key, changes)),
    [edit]
  )
  const removePoint = useCallback(
    (key: LotoPointKey) => edit((data) => removeIsolationPoint(data, key)),
    [edit]
  )
  const movePoint = useCallback(
    (from: number, to: number) =>
      edit((data) => moveIsolationPoint(data, from, to)),
    [edit]
  )
  const toggleNode = useCallback(
    (node: IsolationNodeSummary, systemDocumentId: string) =>
      edit((data) => toggleLotoNode(data, node, systemDocumentId, pickMode)),
    [edit, pickMode]
  )
  const removeTarget = useCallback(
    (key: LotoPointKey) => edit((data) => removeTargetEquipment(data, key)),
    [edit]
  )
  const addPage = useCallback(
    (attachment: LotoAttachment) =>
      edit((data) => addAttachments(data, [attachment])),
    [edit]
  )
  const saveChanges = useCallback(
    () => (hasChanges ? saveWorking() : Promise.resolve(true)),
    [hasChanges, saveWorking]
  )
  const discard = useCallback(() => {
    if (reportId !== undefined) updateDraft(reportId, () => ({}))
  }, [reportId, updateDraft])

  return useMemo(
    () =>
      reportId === undefined
        ? undefined
        : {
            reportId,
            report,
            hasChanges,
            saving,
            saveError,
            save: saveChanges,
            discard,
            addPage,
            updatePoint,
            removePoint,
            movePoint,
            toggleNode,
            pickMode,
            setPickMode,
            removeTarget,
          },
    [
      reportId,
      report,
      hasChanges,
      saving,
      saveError,
      saveChanges,
      discard,
      addPage,
      updatePoint,
      removePoint,
      movePoint,
      toggleNode,
      pickMode,
      setPickMode,
      removeTarget,
    ]
  )
}

export function useLotoIsolationController(
  session: LotoSession | undefined,
  systemDocumentId: string | undefined
): IsolationController | undefined {
  const report = session?.report?.data
  const toggleNode = session?.toggleNode
  const pickMode = session?.pickMode ?? 'points'
  return useMemo(
    () =>
      report && toggleNode && systemDocumentId
        ? {
            selection: isolationSelection(report.isolation_points),
            highlightedNodeIds: report.target_equipment.map(
              ({ node_id }) => node_id
            ),
            pickColor: pickModeToColor[pickMode],
            onToggleNode: (node: IsolationNodeSummary): void =>
              toggleNode(node, systemDocumentId),
          }
        : undefined,
    [report, toggleNode, systemDocumentId, pickMode]
  )
}
